import { useState } from 'react'
import { RotateCcw, Trash2, Loader2 } from 'lucide-react'
import {
  useGetDeletedFacesQuery,
  useRestoreFacesMutation,
  usePermanentDeleteFacesMutation,
} from '@/store/api'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

export default function DeletedFacesView() {
  useSignedUrls()
  const { data, isLoading } = useGetDeletedFacesQuery()
  const [restoreFaces] = useRestoreFacesMutation()
  const [permanentDeleteFaces] = usePermanentDeleteFacesMutation()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const faces = data?.faces ?? []

  const toggleSelect = (faceId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(faceId)) {
        next.delete(faceId)
      } else {
        next.add(faceId)
      }
      return next
    })
  }

  const handleRestore = async (faceIds: string[]) => {
    await restoreFaces({ faceIds }).unwrap()
    setSelectedIds(new Set())
  }

  const handlePermanentDelete = async (faceIds: string[]) => {
    if (!window.confirm(`Permanently delete ${faceIds.length} face(s)? This cannot be undone.`)) {
      return
    }
    await permanentDeleteFaces({ faceIds }).unwrap()
    setSelectedIds(new Set())
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (faces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-center">
        <p className="text-sm text-muted-foreground">No deleted faces</p>
      </div>
    )
  }

  const selectedArray = Array.from(selectedIds)

  return (
    <div>
      {/* Bulk actions */}
      {selectedArray.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-secondary rounded-lg">
          <span className="text-sm text-foreground">
            {selectedArray.length} selected
          </span>
          <button
            onClick={() => handleRestore(selectedArray)}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <RotateCcw size={12} />
            Restore
          </button>
          <button
            onClick={() => handlePermanentDelete(selectedArray)}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 size={12} />
            Delete Permanently
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-4">
        {faces.map((face) => {
          const isSelected = selectedIds.has(face.face_id)
          const deletedDate = face.deleted_at ? new Date(face.deleted_at) : null
          const daysAgo = deletedDate
            ? Math.floor((Date.now() - deletedDate.getTime()) / (1000 * 60 * 60 * 24))
            : null

          return (
            <div
              key={face.face_id}
              onClick={() => toggleSelect(face.face_id)}
              className="flex flex-col items-center gap-1.5 cursor-pointer group"
            >
              <div
                className={`relative w-16 h-16 rounded-full overflow-hidden bg-secondary border-2 transition-colors ${
                  isSelected ? 'border-cyan-500' : 'border-border group-hover:border-muted-foreground'
                }`}
              >
                <img
                  src={signedSrc(face.crop_url)}
                  alt=""
                  className="w-full h-full object-cover opacity-60"
                  loading="lazy"
                />
                {isSelected && (
                  <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">✓</span>
                    </div>
                  </div>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground text-center">
                {daysAgo !== null ? `${daysAgo}d ago` : ''}
              </span>
              {face.person_name && (
                <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                  {face.person_name}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
